import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { Point } from '../../models/Point';
import { Line } from '../../models/shapes/Line';

/**
 * Manages user interaction state (mouse, clicks, transformations)
 */
export class InteractionState extends BaseState {
  // RxJS subjects for reactive state management
  public mouse$ = new BehaviorSubject<Point | null>(null);
  public isMouseDown$ = new BehaviorSubject<boolean>(false);
  public ctrl$ = new BehaviorSubject<boolean>(false);

  private state = {
    // Mouse state
    isMouseDown: false,
    mouse: null as Point | null,
    magnet: null as Point | null,
    magnetPosition: null as Point | null,
    anglePosition: null as Point | null,
    
    // Click states for different operations
    clickMoveStart: null as Point | null,
    clickCopyStart: null as Point | null,
    clickRotateStart: null as Point | null,
    clickMirrorStart: null as Point | null,
    clickScaleStart1: null as Point | null,
    clickScaleStart2: null as Point | null,
    clickScaleBaseDistanceX: null as number | null,
    clickScaleBaseDistanceY: null as number | null,
    clickScaleBaseDistance: null as number | null,
    clickScaleShapeDistance: null as number | null,
    clickLineStart: null as Point | null,
    clickRectangleStart: null as Point | null,
    clickCircleStart: null as Point | null,
    clickSquareStart: null as Point | null,
    clickMoveRouteStart: null as Point | null,
    clickCalcNetworksStart: null as Point | null,
    clickCalcNetworkEnd: null as Point | null,
    clickAssignCpoint1: null as Point | null,
    clickAssignCpoint2: null as Point | null,
    clickAssignCpoint3: null as Point | null,
    
    // Start and end points
    start: null as Point | null,
    end: null as Point | null,
    
    // Connection points
    cpoints: {
      p1: null as Point | null,
      p2: null as Point | null,
    },
    cpointsMod: {
      p1: null as Point | null,
      p2: null as Point | null,
    },
    cpointsFinal: {
      p1: null as Point | null,
      p2: null as Point | null,
    },
    cpointsClick: null as string | null,
    cpointsRotateMatrix: null as any,
    cpointsInitAngleDeg: null as number | null,
    cSeg1: null as Line | null,
    cSeg2: null as Line | null,
    cSegs: null as Line[] | null,
    
    // Network connection state
    startConnectedToAnotherSegment: false,
    endConnectedToAnotherSegment: false,
    startLastTouchedSegment: null as Line | null,
    endLastTouchedSegment: null as Line | null,
    joinedShapeId: null as string | null,
    
    // Input state
    currentInputField: null as HTMLInputElement | null,
    userInputValue: null as number | null,
    
    // Control state
    ctrl: false,
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.emitStateChange(this.state);
  }

  // Mouse state
  setMouseDown(down: boolean) {
    this.state.isMouseDown = down;
    this.isMouseDown$.next(down);
  }

  isMouseDown(): boolean {
    return this.state.isMouseDown;
  }

  setMouse(mouse: Point | null) {
    this.state.mouse = mouse;
    this.mouse$.next(mouse);
  }

  getMouse(): Point | null {
    return this.state.mouse;
  }

  setMagnet(magnet: Point | null) {
    this.state.magnet = magnet;
    this.emitStateChange({ magnet });
  }

  getMagnet(): Point | null {
    return this.state.magnet;
  }

  setMagnetPosition(position: Point | null) {
    this.state.magnetPosition = position;
    this.emitStateChange({ magnetPosition: position });
  }

  getMagnetPosition(): Point | null {
    return this.state.magnetPosition;
  }

  setAnglePosition(position: Point | null) {
    this.state.anglePosition = position;
    this.emitStateChange({ anglePosition: position });
  }

  getAnglePosition(): Point | null {
    return this.state.anglePosition;
  }

  // Click states
  setClickMoveStart(point: Point | null) {
    this.state.clickMoveStart = point;
    this.emitStateChange({ clickMoveStart: point });
  }

  getClickMoveStart(): Point | null {
    return this.state.clickMoveStart;
  }

  setClickCopyStart(point: Point | null) {
    this.state.clickCopyStart = point;
    this.emitStateChange({ clickCopyStart: point });
  }

  getClickCopyStart(): Point | null {
    return this.state.clickCopyStart;
  }

  setClickRotateStart(point: Point | null) {
    this.state.clickRotateStart = point;
    this.emitStateChange({ clickRotateStart: point });
  }

  getClickRotateStart(): Point | null {
    return this.state.clickRotateStart;
  }

  setClickLineStart(point: Point | null) {
    this.state.clickLineStart = point;
    this.emitStateChange({ clickLineStart: point });
  }

  getClickLineStart(): Point | null {
    return this.state.clickLineStart;
  }

  // Start/End points
  setStart(point: Point | null) {
    this.state.start = point;
    this.emitStateChange({ start: point });
  }

  getStart(): Point | null {
    return this.state.start;
  }

  setEnd(point: Point | null) {
    this.state.end = point;
    this.emitStateChange({ end: point });
  }

  getEnd(): Point | null {
    return this.state.end;
  }

  assignEnd(mouse: Point): Point {
    if (this.state.magnetPosition) {
      this.state.end = this.state.magnetPosition;
    } else if (this.state.anglePosition) {
      this.state.end = this.state.anglePosition;
    } else {
      this.state.end = mouse;
    }
    return this.state.end.clone();
  }

  // Connection points
  setCpoint1(point: Point | null) {
    this.state.cpoints.p1 = point?.clone() || null;
    this.emitStateChange({ cpoints: this.state.cpoints });
  }

  setCpoint2(point: Point | null) {
    this.state.cpoints.p2 = point?.clone() || null;
    this.emitStateChange({ cpoints: this.state.cpoints });
  }

  getCpoints() {
    return { ...this.state.cpoints };
  }

  setCpointClick(click: string | null) {
    this.state.cpointsClick = click;
    this.emitStateChange({ cpointsClick: click });
  }

  getCpointClick(): string | null {
    return this.state.cpointsClick;
  }

  setCpointInitAngleDeg(angle: number | null) {
    this.state.cpointsInitAngleDeg = angle;
    this.emitStateChange({ cpointsInitAngleDeg: angle });
  }

  getCpointInitAngleDeg(): number | null {
    return this.state.cpointsInitAngleDeg;
  }

  // Network connection state
  setLastTouchedSegment(segWithConnectedSide: Line) {
    if (!this.state.clickLineStart) {
      this.state.startLastTouchedSegment = segWithConnectedSide;
    } else {
      this.state.endLastTouchedSegment = segWithConnectedSide;
    }
    this.emitStateChange({ lastTouchedSegment: segWithConnectedSide });
  }

  getStartLastTouchedSegment(): Line | null {
    return this.state.startLastTouchedSegment;
  }

  getEndLastTouchedSegment(): Line | null {
    return this.state.endLastTouchedSegment;
  }

  setJoinedShapeId(id: string | null) {
    this.state.joinedShapeId = id;
    this.emitStateChange({ joinedShapeId: id });
  }

  getJoinedShapeId(): string | null {
    return this.state.joinedShapeId;
  }

  // Input state
  setCurrentInputField(field: HTMLInputElement | null) {
    this.state.currentInputField = field;
    this.emitStateChange({ currentInputField: field });
  }

  getCurrentInputField(): HTMLInputElement | null {
    return this.state.currentInputField;
  }

  setUserInputValue(value: number | null) {
    this.state.userInputValue = value;
    this.emitStateChange({ userInputValue: value });
  }

  getUserInputValue(): number | null {
    return this.state.userInputValue;
  }

  // Control state
  setCtrl(pressed: boolean) {
    this.state.ctrl = pressed;
    this.ctrl$.next(pressed);
  }

  isCtrlPressed(): boolean {
    return this.state.ctrl;
  }

  // Reset methods
  reset() {
    this.state.clickMoveStart = null;
    this.state.clickCopyStart = null;
    this.state.clickRotateStart = null;
    this.state.clickMirrorStart = null;
    this.state.magnetPosition = null;
    this.state.anglePosition = null;
    this.state.clickScaleStart1 = null;
    this.state.clickScaleStart2 = null;
    this.state.clickLineStart = null;
    this.state.clickRectangleStart = null;
    this.state.clickCircleStart = null;
    this.state.clickSquareStart = null;
    this.state.clickMoveRouteStart = null;
    this.state.currentInputField = null;
    this.emitStateChange({ reset: true });
  }

  resetCpoints() {
    this.state.cpoints.p1 = null;
    this.state.cpoints.p2 = null;
    this.state.cpointsMod.p1 = null;
    this.state.cpointsMod.p2 = null;
    this.state.cpointsFinal.p1 = null;
    this.state.cpointsFinal.p2 = null;
    this.state.cpointsClick = null;
    this.state.cpointsRotateMatrix = null;
    this.emitStateChange({ cpointsReset: true });
  }
}
